import { useContext, useEffect, useMemo, useState } from 'react';
import { NavLink } from 'react-router-dom';
import TemplatesIcon from './Icons/templates.svg';
import TemplatesActiveIcon from './Icons/active/templates.svg';
import BrandKitIcon from './Icons/brandKit.svg';
import BrandKitActiveIcon from './Icons/active/brandKit.svg';
import CatalogueManagementIcon from './Icons/catalogueManagement.svg';
import CatalogueManagementActiveIcon from './Icons/active/catalogueManagement.svg';
import AdTagIcon from './Icons/adTag.svg';
import AdTagActiveIcon from './Icons/active/adTag.svg';
import VideoTagIcon from './Icons/videoTag.svg';
import VideoTagActiveIcon from './Icons/active/videoTag.svg';
import ContentStudioIcon from './Icons/contentStudio.svg';
import ContentStudioActiveIcon from './Icons/active/contentStudio.svg';
import MetricsIcon from './Icons/metrics.svg';
import MetricsActiveIcon from './Icons/active/metrics.svg';
import './index.css';
import { canViewMetrics, useFeatureFlags, UserContext } from '@adflow/utils';

interface IRoute {
  title: string;
  icon: string;
  activeIcon: string;
  link: string;
  enabled: boolean;
}

export default function SideNavigation() {
  useFeatureFlags();
  const [isVideoTagsEnabled, setIsVideoTagsEnabled] = useState(false);
  const [isAdTagsEnabled, setIsAdTagsEnabled] = useState(false);
  const [isCataloguesEnabled, setIsCataloguesEnabled] = useState(false);
  const [isContentStudioEnabled, setIsContentStudioEnabled] = useState(import.meta.env.DEV);
  const [metricsAccess, setMetricsAccess] = useState<{
    user: unknown;
    enabled: boolean;
  } | null>(null);
  const [isClosed, setIsClosed] = useState(
    localStorage.getItem('mc-navigation-state') === 'closed',
  );

  useEffect(() => {
    const navigationChangeEvt = (evt: MessageEvent) => {
      if (evt?.data?.key === 'mc-navigation-state') {
        if (evt.data.value === 'closed') setIsClosed(true);
        else setIsClosed(false);
      }
    };
    window.addEventListener('message', navigationChangeEvt);
    return () => {
      window.removeEventListener('message', navigationChangeEvt);
    };
  }, []);

  const { user } = useContext(UserContext);

  useEffect(() => {
    let isActive = true;

    const checkVideoTagsEnabled = async () => {
      const canRead = await user?.canRead('creative-hub', 'video-tag');
      setIsVideoTagsEnabled(canRead ?? false);
    };
    checkVideoTagsEnabled();
    const checkAdTagsEnabled = async () => {
      const canRead = await user?.canRead('creative-hub', 'ad-tag');
      setIsAdTagsEnabled(canRead ?? false);
    };
    checkAdTagsEnabled();
    const checkCataloguesEnabled = async () => {
      const canRead = await user?.canRead('creative-hub', 'catalogue');
      setIsCataloguesEnabled(canRead ?? false);
    };
    checkCataloguesEnabled();
    const checkContentStudioEnabled = async () => {
      const enabled =
        ((await user?.hasFeature('cm-content-studio')) &&
          ((await user?.canRead('creative-hub', 'ad-tag')) ||
            (await user?.canRead('creative-hub', 'catalogue')))) ??
        false;
      setIsContentStudioEnabled(enabled);
    };
    checkContentStudioEnabled();
    const checkMetricsEnabled = async () => {
      try {
        const enabled = await canViewMetrics(user);
        if (isActive) {
          setMetricsAccess({ user, enabled });
        }
      } catch (error) {
        console.error('Failed to check Metrics navigation access', error);
        if (isActive) {
          setMetricsAccess({ user, enabled: false });
        }
      }
    };
    checkMetricsEnabled();

    return () => {
      isActive = false;
    };
  }, [user]);

  const isMetricsEnabled = metricsAccess?.user === user && metricsAccess.enabled;

  const routes: IRoute[] = [
    {
      title: 'Templates',
      icon: TemplatesIcon,
      activeIcon: TemplatesActiveIcon,
      link: '/templates',
      enabled: isAdTagsEnabled || isCataloguesEnabled,
    },
    {
      title: 'Brand Kits',
      icon: BrandKitIcon,
      activeIcon: BrandKitActiveIcon,
      link: '/brand-kits',
      enabled: isAdTagsEnabled || isCataloguesEnabled,
    },
    {
      title: 'Content Studio',
      icon: ContentStudioIcon,
      activeIcon: ContentStudioActiveIcon,
      link: '/content-studio',
      enabled: isContentStudioEnabled,
    },
    {
      title: 'Catalogues',
      icon: CatalogueManagementIcon,
      activeIcon: CatalogueManagementActiveIcon,
      link: '/catalogues',
      enabled: isCataloguesEnabled,
    },
    {
      title: 'Ad Tags',
      icon: AdTagIcon,
      activeIcon: AdTagActiveIcon,
      link: '/ad-tags',
      enabled: isAdTagsEnabled,
    },
    {
      title: 'Video Tags',
      icon: VideoTagIcon,
      activeIcon: VideoTagActiveIcon,
      link: '/video-tags',
      enabled: isVideoTagsEnabled,
    },
    {
      title: 'Metrics',
      icon: MetricsIcon,
      activeIcon: MetricsActiveIcon,
      link: '/metrics',
      enabled: isMetricsEnabled,
    },
  ];

  const renderRoute = (route: IRoute) => {
    return (
      <NavLink to={route.link} key={route.title}>
        <div className="sideBarItem">
          <div className="sideBarOverlay" />
          <div className="sideBarIcon_container">
            <img
              className="sideBarIcon sideBarIcon--default"
              src={route.icon}
              alt={`${route.title} icon`}
            />
            <img
              className="sideBarIcon sideBarIcon--hover"
              src={route.activeIcon}
              alt={`${route.title} active icon`}
              aria-hidden
            />
          </div>
          <div className="sideBarTitle">{route.title}</div>
        </div>
      </NavLink>
    );
  };

  const className = useMemo(() => (isClosed ? 'closed' : ''), [isClosed]);

  return (
    <div id="sidebar" className={className}>
      {routes.filter((route) => route.enabled).map(renderRoute)}
    </div>
  );
}
